import React, { createContext, useContext, useEffect, useState } from 'react';

// The URL of our new Cloudflare API
// Replace with dev URL if testing locally
export const API_URL = import.meta.env.VITE_API_URL || 'https://antigravity-api.gutlessbook.workers.dev';

// Global Fetch Interceptor to automatically attach Bearer token to all API calls
const originalFetch = window.fetch;
window.fetch = async (input, init) => {
    if (typeof input === 'string' && input.startsWith(API_URL)) {
        const token = localStorage.getItem('auth_token');
        if (token) {
            init = init || {};

            // If headers is a Headers object, we need to append to it, or just spread standard objects
            if (init.headers instanceof Headers) {
                init.headers.set('Authorization', `Bearer ${token}`);
            } else {
                init.headers = {
                    ...init.headers,
                    'Authorization': `Bearer ${token}`
                };
            }
        }
    }
    return originalFetch(input, init);
};

export interface User {
    sub: string;
    pseudonym: string;
    email: string;
    role: 'author' | 'reader';
    coffeeBalance: number;
}

interface AuthContextType {
    user: User | null;
    isLoading: boolean;
    login: (user: User, token: string) => void;
    logout: () => Promise<void>;
    refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const refreshAuth = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('auth_token');
            if (!token) {
                setUser(null);
                setIsLoading(false);
                return;
            }

            const res = await fetch(`${API_URL}/api/auth/me`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (res.ok) {
                const data = await res.json();
                setUser(data.user);
            } else {
                localStorage.removeItem('auth_token');
                setUser(null);
            }
        } catch (error) {
            console.error('Auth refresh failed:', error);
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    };

    const login = (userData: User, token: string) => {
        localStorage.setItem('auth_token', token);
        setUser(userData);
    };

    const logout = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            if (token) {
                await fetch(`${API_URL}/api/auth/logout`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
            }
        } catch (e) {
            console.error("Logout request failed", e);
        }
        localStorage.removeItem('auth_token');
        setUser(null);
        window.location.href = '/login';
    };

    useEffect(() => {
        refreshAuth();
    }, []);

    return (
        <AuthContext.Provider value={{ user, isLoading, login, logout, refreshAuth }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
